import { useEffect, useRef, useState } from "react";

import { HttpApiService } from "@/shared/services/httpApi.service";

import { BrebKeysService, type BrebKeyResolution } from "../services/brebKeys.service";
import { type BrebKeyType, isBrebKeyValid } from "../services/paymentsPayout.service";
import { looksLikeBrebKey } from "../utils/brebKeyLookup";
import type { BrebDirectoryOutcome } from "../utils/colombiaDestination";

const RESOLVE_DEBOUNCE_MS = 600;

/** A directory answer that will not change on a retry. */
export type SettledBrebResolution =
  | { status: "resolved"; data: BrebKeyResolution }
  /** The directory does not recognize the key — the payout WILL be rejected. */
  | { status: "not_found" };

export type BrebKeyResolutionState =
  | { status: "idle" }
  | { status: "loading" }
  | SettledBrebResolution
  /**
   * The directory could not be reached. Not an answer about the key — an absence of one, which is why it is
   * never cached and why the hook keeps asking on a backoff (`brebRetryDelay`) until it gets one.
   */
  | { status: "unavailable" };

/**
 * How long a `not_found` stays good before the directory is asked again.
 *
 * Short, because this answer now BLOCKS the send: a key the recipient registers while the operator waits has
 * to stop blocking on its own. A `resolved` never expires — see `expiresAt`.
 */
const NOT_FOUND_TTL_MS = 2 * 60 * 1000;

interface CachedResolution {
  settled: SettledBrebResolution;
  /**
   * `Infinity` for an answer that cannot go stale.
   *
   * That is `resolved`, deliberately. If it expired, a read on step 3 would fall back to `loading` and the
   * card would lose the holder the operator just confirmed — which is the regression this cache exists to
   * prevent. A masked name a few minutes old is harmless; one that vanishes from the confirmation screen is
   * not.
   */
  expiresAt: number;
}

/**
 * Deterministic answers, shared by every caller and keyed by the normalized key.
 *
 * Three places need the same answer — the field in step 2, the confirmation card in step 3, and the
 * validation that decides whether Continue is enabled. Without this, step 3 re-queried from scratch on mount:
 * the card painted for 600 ms + RTT without the holder, and if the directory happened to be down at that
 * instant the identity the user had just confirmed in step 2 silently disappeared from the screen they
 * confirm on.
 *
 * **A failure is never cached.** Only `resolved` and `not_found` land here, because only those are answers
 * about the key; `unavailable` is a transient fact about the directory, and caching it would pin a momentary
 * outage to a key for the rest of the session.
 */
const RESOLUTION_CACHE = new Map<string, CachedResolution>();

/** When an answer stops being good. Pure, so the TTL is testable without a clock in the hook. */
export function resolutionExpiry(settled: SettledBrebResolution, now: number): number {
  return settled.status === "not_found" ? now + NOT_FOUND_TTL_MS : Infinity;
}

/**
 * How long the TTL timer waits before retiring a cached answer, or null when that answer cannot go stale.
 *
 * Pure for the same reason `resolutionExpiry` is: it is the arithmetic the effect runs, and a test that
 * re-implemented it would only have asserted its own copy. The `+ 1` keeps the timer from firing on the
 * boundary instant, where the entry is still live; a window that closed while the tab was in the background
 * comes back as 1 ms, not as a negative delay.
 */
export function resolutionRetireDelay(expiresAt: number, now: number): number | null {
  return expiresAt === Infinity ? null : Math.max(expiresAt - now, 0) + 1;
}

/** Waits between retries while the directory is unreachable, then a steady beat. */
const UNAVAILABLE_RETRY_MS = [2_000, 5_000, 15_000, 30_000] as const;
const UNAVAILABLE_RETRY_CEILING_MS = 60_000;

/**
 * How long to wait before asking the directory again after it failed to answer, by attempt number.
 *
 * **It never gives up, and that is the design.** `unavailable` is not an answer about the key, so it must
 * never become a dead end: it is not cached, which means the TTL timer — which only retires CACHED answers —
 * does not re-arm anything, `normalized` has not changed, and re-typing the same key normalizes to the very
 * same string. So before this, a one-second outage blocked the send until the operator deleted the key and
 * wrote it again, while the message on screen told them to try in a few minutes. Backing off to a one-minute
 * beat is what makes that sentence true without turning a long outage into a poll — and it beats a "Retry"
 * button, which would have to be pressed by a person watching a form they cannot submit.
 *
 * Pure, so the cadence is asserted without a clock in the hook.
 */
export function brebRetryDelay(attempt: number): number {
  return UNAVAILABLE_RETRY_MS[attempt] ?? UNAVAILABLE_RETRY_CEILING_MS;
}

function cacheResolution(normalized: string, settled: SettledBrebResolution): SettledBrebResolution {
  RESOLUTION_CACHE.set(normalized, { settled, expiresAt: resolutionExpiry(settled, Date.now()) });
  return settled;
}

/** In-flight requests, so three components asking at once produce one call. */
const IN_FLIGHT = new Map<string, Promise<BrebKeyResolutionState>>();

function resolveOnce(normalized: string): Promise<BrebKeyResolutionState> {
  const existing = IN_FLIGHT.get(normalized);
  if (existing) return existing;

  const request = BrebKeysService.resolve(normalized)
    .then(
      (res): BrebKeyResolutionState => cacheResolution(normalized, { status: "resolved", data: res.data }),
    )
    .catch((error: unknown): BrebKeyResolutionState => {
      const notFound = HttpApiService.isAxiosError(error) && error.response?.status === 404;
      if (!notFound) return { status: "unavailable" };
      return cacheResolution(normalized, { status: "not_found" });
    })
    .finally(() => {
      IN_FLIGHT.delete(normalized);
    });

  IN_FLIGHT.set(normalized, request);
  return request;
}

type SettledResolution = { key: string; state: BrebKeyResolutionState };

/**
 * The two states that carry no data, hoisted so they keep one identity.
 *
 * As object literals they were a new object on every render, and this hook is idle for most of the wizard's
 * life: `normalized` is empty on Brazil, Mexico, Argentina and Colombia/Transfiya, and on Bre-B until a
 * format-valid key exists. That identity is now a dependency of the `formErrors` memo in
 * `useDispersionFormErrors`, whose result is the `errors` prop of the two `React.memo` steps — so a fresh
 * object per render made all three memos inert, while their own comments went on asserting that the props
 * kept identity.
 */
const IDLE_RESOLUTION: BrebKeyResolutionState = { status: "idle" };
const LOADING_RESOLUTION: BrebKeyResolutionState = { status: "loading" };

/**
 * The key to ask about: the value as typed, only without the blanks around it, and only once it is a key as
 * written. Nothing inside is rewritten, since keys are unique. `null` type: the key was typed without saying
 * what it is, and the directory's answer will say.
 */
export function normalizedLookupKey(keyType: BrebKeyType | null, keyValue: string): string {
  const key = keyValue.trim();
  if (keyType === null) return looksLikeBrebKey(key) ? key : "";
  return isBrebKeyValid(keyType, key) ? key : "";
}

/**
 * Resolves a Bre-B key in the directory (debounced) so the form can show the masked holder identity before
 * the payout is created.
 *
 * **No longer purely informative.** The holder it returns is the only name the Bre-B flow has — the form
 * stopped asking for one — so a key the directory refuses, or cannot be asked about, blocks the send. The
 * gate itself lives in `useDispersionFormErrors`; this hook only reports.
 */
export function useBrebKeyResolution(
  keyType: BrebKeyType | null,
  keyValue: string,
  enabled: boolean,
): BrebKeyResolutionState {
  // Only settled fetches are stored; idle/loading are derived from the current key, so the effect never
  // sets state synchronously. The settled value is keyed by the key it resolved.
  const [settled, setSettled] = useState<SettledResolution | null>(null);
  // Serial guard: a stale response (slower request for a previous key) must never win.
  const requestIdRef = useRef(0);
  // Bumped when a cached answer reaches its TTL, or when an outage is retried. It is what re-arms the fetch
  // below.
  const [staleAt, setStaleAt] = useState(0);
  // Retries spent on the key currently in the field. Keyed by the key, the way `settled` is, so a new key
  // starts its backoff over without an effect whose only job is to reset a counter.
  const [outage, setOutage] = useState<{ key: string; attempts: number }>({ key: "", attempts: 0 });

  const normalized = enabled ? normalizedLookupKey(keyType, keyValue) : "";
  // No clock in here. Reading `Date.now()` while rendering makes the render non-idempotent, so expiry is
  // enforced by DELETING the entry in the effect below — which is why this read needs no TTL check.
  const cached = normalized ? RESOLUTION_CACHE.get(normalized)?.settled : undefined;

  // The one derivation of what this hook reports, read by the retry effect below as well as by the caller.
  // The cache comes first on purpose: once any instance stores an answer, a local `settled` holding a stale
  // outage must not outrank it.
  const resolution: BrebKeyResolutionState = !normalized
    ? IDLE_RESOLUTION
    : (cached ?? (settled?.key === normalized ? settled.state : LOADING_RESOLUTION));
  const outageAttempts = outage.key === normalized ? outage.attempts : 0;

  // Retires an answer once it reaches its TTL. Without this the TTL would be invisible to the field on
  // screen: `normalized` has not changed, so the fetch effect below would not re-run, and re-typing the
  // same key normalizes to the very same string — so today a `not_found` only clears on a reload. Clearing
  // `settled` is the other half: it is a local mirror of the cache and would keep serving the dead answer.
  //
  // `settled` is in the deps because a freshly stored answer is exactly what gives this something to
  // schedule; the entry itself is shared with the other callers, so it is read from the cache. The timer
  // never fires synchronously, which is what keeps the state writes out of the effect body.
  useEffect(() => {
    if (!normalized) return;
    const entry = RESOLUTION_CACHE.get(normalized);
    const delay = entry ? resolutionRetireDelay(entry.expiresAt, Date.now()) : null;
    if (delay == null) return;
    const timer = setTimeout(() => {
      RESOLUTION_CACHE.delete(normalized);
      setSettled(null);
      setStaleAt(Date.now());
    }, delay);
    return () => clearTimeout(timer);
  }, [normalized, settled, staleAt]);

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    // A cached answer needs no request and no debounce — it is already on screen this render. Expired ones
    // are not here: the effect above removes them.
    if (!normalized || RESOLUTION_CACHE.has(normalized)) return;
    const timer = setTimeout(() => {
      void resolveOnce(normalized).then((state) => {
        if (requestIdRef.current !== requestId) return;
        setSettled({ key: normalized, state });
      });
    }, RESOLVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [normalized, staleAt]);

  // Asks again while the directory is down, backing off. `settled` is deliberately NOT cleared: the card
  // keeps the outage message instead of flashing back to its skeleton on every attempt, and the moment any
  // instance stores a real answer the cache read above outranks the stale one. The timer is per instance —
  // the three that mount together (the field, the confirmation card and the gate) share one request through
  // `IN_FLIGHT`, and each converges on its own next tick.
  useEffect(() => {
    if (!normalized || resolution.status !== "unavailable") return;
    const timer = setTimeout(() => {
      setOutage({ key: normalized, attempts: outageAttempts + 1 });
      setStaleAt(Date.now());
    }, brebRetryDelay(outageAttempts));
    return () => clearTimeout(timer);
  }, [normalized, resolution.status, outageAttempts]);

  return resolution;
}

/**
 * The status the directory reports when our own backend could not read one off the ledger entry.
 *
 * It is synthesized by `bank-directory.service.ts`, not sent by the network, so it must never be rendered as
 * a rejection: we do not know that the key is inactive, only that we could not tell.
 */
export const BREB_STATUS_UNKNOWN = "UNKNOWN";

/**
 * The directory's answer reduced to what the two gates ask of it.
 *
 * The thing worth not re-deriving: a key the directory KNOWS but reports as non-ACTIVE comes back as a 200,
 * so its resolution state is `resolved` — the one value both gates treat as "go". Passing `resolution.status`
 * straight into them therefore let an inactive key through, on the same screen where
 * `BrebRecipientConfirmation` was already saying the send would be rejected. The screen and the gate have to
 * read the directory the same way, so this is the single place that reads it.
 *
 * `UNKNOWN` keeps passing, and that is not an oversight: it is synthesized by our own backend when it could
 * not read a status off the ledger entry, so it says nothing about the key. Failing closed on an absence of
 * information would take the corridor down whenever that lookup degrades.
 */
export function brebDirectoryOutcome(resolution: BrebKeyResolutionState): BrebDirectoryOutcome {
  if (resolution.status !== "resolved") return resolution.status;
  // Only a positive statement that the key is not active refuses the send. The other three readings are
  // things the OPERATOR should see and not reasons to stop: `no_holder` says the key IS active and the
  // directory merely withheld the name, and `status_unknown` is an absence of information.
  return brebResolvedKind(resolution.data) === "inactive" ? "inactive" : "resolved";
}

/** How a RESOLVED directory answer reads — the four cases `BrebRecipientConfirmation` draws. */
export type BrebResolvedKind = "confirmed" | "status_unknown" | "inactive" | "no_holder";

/**
 * The single reading of a resolved directory answer, for the card AND for the gates.
 *
 * It exists because two independent readings is what the gate bug was, and fixing one of them left the other:
 * the card compared `data.status !== "ACTIVE"` exactly while the gate upper-cased, so a directory answering
 * `"active"` made the card say _"the key exists but is not active. The payout would be rejected."_ with the
 * Authorize button enabled underneath it — the same inversion, pointing the other way.
 *
 * **The status is the PROVIDER's raw string.** coloca-nest writes `status: asText(custom.status) ||
 * 'UNKNOWN'` with no whitelist and no normalisation, so `"active"` and `"ACTIVE "` are both answers it can
 * return; the same file already tolerates a misspelled `enitityName`, so loose data from that payload is not
 * hypothetical. Hence `.trim().toUpperCase()` — in ONE place, which is the point.
 */
export function brebResolvedKind(data: BrebKeyResolution): BrebResolvedKind {
  const status = String(data.status ?? "")
    .trim()
    .toUpperCase();
  // An empty status is an ABSENCE of information, which is the same class as `UNKNOWN` and must not read as
  // a refusal — the function's own doctrine, which the first version contradicted by blocking on it. The
  // backend already substitutes `'UNKNOWN'` for an empty value, so this is belt and braces.
  if (!status || status === BREB_STATUS_UNKNOWN) return "status_unknown";
  if (status !== "ACTIVE") return "inactive";
  if (!data.holder_name?.trim()) return "no_holder";
  return "confirmed";
}
